import Link from "next/link";
import PricingCard from "../components/PricingCard";
import Seo from "../components/Seo";
import { useLineQuoteUrl } from "lib/line";

const plans = [
  {
    name: "จดทะเบียนธุรกิจ",
    pricePrefix: "ราคาแพ็กเกจ",
    price: "9,500 บาท",
    description: "สำหรับจดทะเบียนบริษัทหรือห้างหุ้นส่วน โดยทีมงานจะแจ้งรายการเอกสารและขั้นตอนก่อนยื่นจดทะเบียน",
    features: [
      "จองชื่อและยื่นจดทะเบียนธุรกิจ",
      "จัดเตรียมเอกสารประกอบการจดทะเบียน",
      "ประเมินงาน VAT และประกันสังคมที่อาจต้องดำเนินการเพิ่ม",
      "แจ้งสถานะและรายการที่ต้องดำเนินการหลังจดทะเบียน"
    ]
  },
  {
    name: "บัญชีรายเดือน",
    pricePrefix: "เริ่มต้น",
    price: "2,000 บาท",
    priceSuffix: "ต่อเดือน",
    description: "สำหรับกิจการที่มีเอกสารไม่เกิน 30 บิลต่อเดือน ค่าบริการอาจเปลี่ยนตามประเภทธุรกิจและแบบภาษีที่ต้องยื่น",
    features: [
      "จัดทำบัญชีจากเอกสารที่ได้รับ",
      "จัดทำและยื่นแบบภาษีที่เกี่ยวข้อง",
      "กระทบยอดบัญชีธนาคาร",
      "สรุปยอดขายและค่าใช้จ่ายประจำเดือน"
    ],
    popular: true
  },
  {
    name: "ปิดบัญชีประจำปี",
    pricePrefix: "เริ่มต้น",
    price: "9,000 บาท",
    description: "สำหรับกิจการที่ต้องจัดทำงบการเงินประจำปี ราคาขึ้นอยู่กับจำนวนรายการและความพร้อมของเอกสาร",
    features: [
      "ตรวจความครบถ้วนและปรับปรุงรายการบัญชีปลายปี",
      "จัดเตรียมงบการเงินและหมายเหตุประกอบงบ",
      "ประสานงานกับผู้สอบบัญชี",
      "ดำเนินการยื่นงบตามขอบเขตที่ตกลง"
    ]
  }
];

const faqs = [
  {
    question: "ค่าบริการบัญชีรายเดือนพิจารณาจากอะไร?",
    answer:
      "พิจารณาจากประเภทธุรกิจ จำนวนเอกสาร จำนวนพนักงาน การจด VAT และแบบภาษีที่เกี่ยวข้อง ทีมงานจะแจ้งขอบเขตก่อนเริ่มงาน"
  },
  {
    question: "อยากเพิ่มบริการอื่นหลังจากเริ่มใช้แล้วได้ไหม?",
    answer:
      "ได้ ทีมงานจะประเมินขอบเขตและค่าใช้จ่ายของงานเพิ่มเติม แล้วแจ้งให้ยืนยันก่อนดำเนินการ"
  },
  {
    question: "ราคาในหน้าเว็บไซต์เป็นราคาสุดท้ายหรือไม่?",
    answer:
      "เป็นราคาเบื้องต้น ค่าใช้จ่ายจริงขึ้นอยู่กับเอกสาร สถานะบัญชี และบริการเพิ่มเติม กรุณาสอบถามรายละเอียดก่อนเริ่มงาน"
  }
];

export default function PricingPage() {
  const title = "ค่าบริการทำบัญชีและจดทะเบียน เชียงใหม่";
  const lineUrl = useLineQuoteUrl();
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.kmcpaccounting.com").replace(/\/$/, "");
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${siteUrl}/` },
          { "@type": "ListItem", position: 2, name: "ค่าบริการ", item: `${siteUrl}/pricing` }
        ]
      },
      {
        "@type": "ItemList",
        name: "ค่าบริการ KMCP Accounting",
        itemListElement: [
          { "@type": "ListItem", position: 1, item: { "@type": "Service", name: "จดทะเบียนธุรกิจ", url: `${siteUrl}/services/registration` } },
          { "@type": "ListItem", position: 2, item: { "@type": "Service", name: "บัญชีรายเดือน", url: `${siteUrl}/services/accounting` } },
          { "@type": "ListItem", position: 3, item: { "@type": "Service", name: "ปิดบัญชีประจำปี", url: `${siteUrl}/pricing#annual-closing` } }
        ]
      }
    ]
  };

  return (
    <>
      <Seo
        title={title}
        description="ค่าบริการทำบัญชีรายเดือนในเชียงใหม่ เริ่มต้น 2,000 บาท จดทะเบียนธุรกิจ 9,500 บาท และปิดบัญชีประจำปีเริ่มต้น 9,000 บาท พร้อมเงื่อนไขประเมินราคา"
        path="/pricing"
        jsonLd={structuredData}
      />
      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 pt-14 pb-16 sm:px-8 sm:pt-16 sm:pb-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-semibold leading-[1.24] text-gray-900 sm:leading-[1.18]">ค่าบริการทำบัญชี จดทะเบียน และปิดบัญชี</h1>
            <p className="mt-4 text-base leading-7 text-gray-600">
              ค่าบริการขึ้นอยู่กับประเภทธุรกิจ จำนวนเอกสาร และงานที่เกี่ยวข้อง ทีมงานจะแจ้งขอบเขตและค่าใช้จ่ายก่อนเริ่มงาน
            </p>
          </div>
          <h2 className="sr-only">แพ็กเกจค่าบริการเบื้องต้น</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {plans.map((plan, index) => (
              <div key={plan.name} id={index === 2 ? "annual-closing" : undefined} className="scroll-mt-28">
              <PricingCard
                name={plan.name}
                price={plan.price}
                pricePrefix={plan.pricePrefix}
                priceSuffix={plan.priceSuffix}
                description={plan.description}
                features={plan.features}
                highlighted={plan.popular}
                cta={
                  <a
                    href={lineUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary w-full text-center transition hover:shadow-lg hover:bg-brand/90"
                  >
                    สอบถามรายละเอียด
                  </a>
                }
              />
              <Link
                href={index === 0 ? "/services/registration" : index === 1 ? "/services/accounting" : "/about"}
                className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-gray-700 hover:text-gray-950 hover:underline"
              >
                {index === 0 ? "ดูรายละเอียดงานจดทะเบียน" : index === 1 ? "ดูรายละเอียดบัญชีรายเดือน" : "ดูข้อมูลสำนักงาน"} →
              </Link>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-sm leading-7 text-gray-700 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900">หมายเหตุเรื่องราคา</h2>
            <p className="mt-2">
              ราคาบนเว็บไซต์เป็นราคาเบื้องต้น ใบเสนอราคาจะระบุขอบเขตงาน จำนวนเอกสาร และแจ้งว่ารวมค่าธรรมเนียมราชการ ค่าสอบบัญชี หรือค่าใช้จ่ายบุคคลภายนอกหรือไม่ ก่อนให้ลูกค้ายืนยันเริ่มงาน
            </p>
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-[1.1fr,1fr]">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900">ขั้นตอนการประเมินและเริ่มงาน</h2>
              <ul className="mt-6 space-y-4 text-sm text-gray-600">
                <li>
                  <span className="font-semibold text-gray-900">1. แจ้งข้อมูลกิจการ:</span> ประเภทธุรกิจ จำนวนเอกสาร และบริการที่ต้องการ
                </li>
                <li>
                  <span className="font-semibold text-gray-900">2. ตรวจข้อมูลเบื้องต้น:</span> ทีมงานขอตัวอย่างเอกสารและตรวจสถานะงานเดิมตามความจำเป็น
                </li>
                <li>
                  <span className="font-semibold text-gray-900">3. ยืนยันขอบเขต:</span> แจ้งรายการงาน ค่าใช้จ่าย และข้อมูลที่ต้องส่งในแต่ละรอบ
                </li>
                <li>
                  <span className="font-semibold text-gray-900">4. เริ่มดำเนินงาน:</span> กำหนดช่องทางส่งเอกสาร ผู้ติดต่อ และกำหนดส่งงานร่วมกัน
                </li>
              </ul>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900">คำถามที่พบบ่อย</h2>
              <div className="mt-6 space-y-6 text-sm text-gray-600">
                {faqs.map((faq) => (
                  <details key={faq.question} className="group rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <summary className="cursor-pointer list-none font-semibold text-gray-900">
                      {faq.question}
                    </summary>
                    <p className="mt-3 text-sm text-gray-600">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
